import type { Context, RUMEntry } from "@fiverr-private/relay-sdk";
import { RelaySDK } from "@fiverr-private/relay-sdk";
import { pageMetaData } from "../pageMetaData";
import { getPageInfo } from "../getPageInfo";
import { includeEvent } from "../includeEvent";

let relaySDK: RelaySDK;

/**
 * Queue data to be sent to the server
 */
export async function push(
    entries: RUMEntry[],
    { immediate = false } = {},
): Promise<void> {
    entries = entries.filter(includeEvent);
    if (!entries.length) return;
    createRelaySDKInstance();
    relaySDK.rum(...entries);
    immediate && relaySDK.send();
}

function createRelaySDKInstance(): Promise<void> {
    if (relaySDK) return;
    const {
        browser_stats_api,
        browser_relay_service_api,
        pagename,
        user_id,
        ctx_id,
        uid,
    } = pageMetaData();
    const pageInfo = getPageInfo();
    const apiBase =
        browser_relay_service_api ||
        `https://${new URL(browser_stats_api).hostname}`; // TODO: remove fallback after all clients have the meta tag
    relaySDK = new RelaySDK({
        id: pageInfo.trace_id,
        endpoint: `${apiBase}/v3/relay`,
    });
    relaySDK.registerContext("rum", {
        client_version: pageInfo.client_version,
        page: {
            name_performance: pagename,
            ctx: ctx_id,
            trace_id: pageInfo.trace_id,
            url: window.location.href,
            referrer: document.referrer,
            locale: pageInfo.language,
        },
        user: {
            id: user_id,
            uid: uid,
        },
        view: {
            height: pageInfo.window_inner_height,
            width: pageInfo.window_inner_width,
            color_depth: pageInfo.screen_color_depth,
            orientation: pageInfo.screen_orientation_type,
            pixel_depth: pageInfo.screen_pixel_depth,
        },
        navigation: {
            type: pageInfo.navigation_type,
            effectiveConnectionType: pageInfo.effective_connection_type,
            effectiveBandwidth: pageInfo.effective_bandwidth,
            rtt: pageInfo.round_trip_time,
            reducedDataMode: pageInfo.reduced_data_usage,
        },
        memory: {
            totalJSHeapSize: pageInfo.total_js_heap_size,
            usedJSHeapSize: pageInfo.used_js_heap_size,
            jsHeapSizeLimit: pageInfo.js_heap_size_limit,
        },
        rumAdditionalInfo: [
            "akamai_hit_rate",
            "browser_cache_hit_rate_javascript_bytes",
            "browser_cache_hit_rate_javascript_files",
            "browser_cache_hit_rate_stylesheets_bytes",
            "browser_cache_hit_rate_stylesheets_files",
            "final_asset_images_count",
            "final_asset_images_load",
            "final_asset_images_size",
            "final_asset_javascript_count",
            "final_asset_javascript_load",
            "final_asset_javascript_size",
            "final_asset_other_count",
            "final_asset_other_load",
            "final_asset_other_size",
            "final_asset_stylesheets_count",
            "final_asset_stylesheets_load",
            "final_asset_stylesheets_size",
            "final_dom_nest_depth",
            "final_dom_node_count",
            "final_html_size",
        ].reduce(
            (
                acc: Context["rumAdditionalInfo"],
                key,
            ): Context["rumAdditionalInfo"] =>
                Object.assign(acc, { [key]: pageInfo[key] ?? undefined }),
            {} as Context["rumAdditionalInfo"],
        ),
    });
}
